import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD, FORM } from "@/constants/testIds";
import Modal from "@/components/Modal";
import EmployeeForm from "@/components/EmployeeForm";
import PartnerProfileModal from "@/components/PartnerProfileModal";

export default function EmployeesPanel({ onChanged }) {
  const { t } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [partnerFor, setPartnerFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/employees`);
      setEmployees(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/employees/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/employees`, payload);
      }
      toast.success(t("save"));
      setOpen(false);
      setEditing(null);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success(t("delete"));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid={DASHBOARD.employeesList} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-overline">EMP / 03</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {t("tab_employees")}
          </h2>
        </div>
        <button
          data-testid={DASHBOARD.newEmployeeBtn}
          onClick={() => {
            setEditing({});
            setOpen(true);
          }}
          className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
        >
          + {t("new_employee")}
        </button>
      </div>

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : employees.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          {t("no_employees")}
        </div>
      ) : (
        <div className="border border-[var(--border)] bg-white divide-y divide-[var(--border)]">
          {employees.map((e) => (
            <div
              key={e.id}
              data-testid={DASHBOARD.employeeRow(e.id)}
              className="grid grid-cols-12 gap-4 items-center px-5 py-4 hover:bg-[var(--bg)]"
            >
              <div className="col-span-12 md:col-span-4">
                <div className="font-display text-lg font-bold tracking-tight">{e.name}</div>
                <div className="font-mono-tech text-xs text-[var(--text-secondary)]">
                  @{e.username}
                </div>
              </div>
              <div className="col-span-6 md:col-span-4 text-sm text-[var(--text-secondary)]">
                {e.role || "—"}
              </div>
              <div className="col-span-6 md:col-span-4 flex justify-end gap-2">
                {(e.role || "").toLowerCase().includes("partner") && (
                  <button
                    data-testid={`emp-partner-profile-${e.id}`}
                    onClick={() => setPartnerFor(e)}
                    className="border border-black px-3 py-1.5 text-sm font-bold hover:bg-black hover:text-white"
                  >
                    Perfil Partner
                  </button>
                )}
                <button
                  data-testid={FORM.empEdit(e.id)}
                  onClick={() => {
                    setEditing(e);
                    setOpen(true);
                  }}
                  className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
                >
                  {t("edit")}
                </button>
                <button
                  data-testid={FORM.empDelete(e.id)}
                  onClick={() => remove(e.id)}
                  className="border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-3 py-1.5 text-sm hover:bg-[var(--alert-overdue)] hover:text-white"
                >
                  {t("delete")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? t("edit_employee") : t("new_employee")}
        testId="employee-modal"
        wide
      >
        <EmployeeForm
          initial={editing}
          onSubmit={submit}
          onCancel={() => {
            setOpen(false);
            setEditing(null);
          }}
        />
      </Modal>
      {partnerFor && (
        <PartnerProfileModal employee={partnerFor} onClose={() => setPartnerFor(null)} />
      )}
    </div>
  );
}
